import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { DeployVersionService } from '../../services/deploy-version.service';
import {
  RELOAD_ZIEL_KEY,
  browserCachesLeeren,
  mitCacheBuster,
  reloadWarWirkungslos,
} from '../../models/deploy-version.models';

/** Poll-Intervall. Fuenf Minuten: Ein neuer Deploy ist kein Ereignis, das
 *  sekundengenau erkannt werden muss, und der Endpoint soll nicht unnoetig
 *  Last erzeugen. */
const POLL_INTERVALL_MS = 5 * 60 * 1000;

/**
 * Hinweisstreifen „neue Version verfuegbar — bitte neu laden".
 *
 * Loest den Vorfall vom 2026-08-09: Nach einem Frontend-Deploy laufen offene
 * Sitzungen auf alten, gehashten Chunks weiter. Das scheitert STILL — im
 * konkreten Fall blieb ein Login ohne Fehlermeldung haengen, waehrend
 * serverseitig alles 200 lieferte. Erst ein harter Reload half. Der Nutzer
 * hatte keine Chance, das zu erkennen.
 *
 * BEWUSST KEIN automatischer Reload: Dieses Portal ist formularlastig
 * (Leistungsbild-Wizard, Kostenerfassung). Ein Reload ohne Zutun kann
 * ungespeicherte Eingaben verwerfen. Der Nutzer entscheidet, wann es passt —
 * deshalb Streifen plus Knopf statt Automatik.
 *
 * KEIN Toast: Ein Toast verschwindet. Wer im Moment nicht hinsieht, verpasst
 * ihn, und danach scheitert es wieder still. Der Hinweis muss stehen bleiben,
 * bis der Nutzer handelt.
 *
 * Gemountet im default-layout neben Wartungs- und 2FA-Banner (dasselbe
 * Muster).
 */
@Component({
  selector: 'app-update-available-banner',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (updateVerfuegbar()) {
      <div class="update-banner" role="status" aria-live="polite" [title]="kennungen()">
        <span class="me-2" aria-hidden="true">⟳</span>
        @if (reloadWirkungslos()) {
          <strong>Neu laden hat den Stand nicht geändert.</strong>
          <span class="ms-2">
            Der Server liefert weiterhin eine andere Version aus. Meistens läuft noch ein
            Deploy — bitte in einigen Minuten erneut versuchen.
          </span>
        } @else {
          <strong>Neue Version verfügbar.</strong>
          <span class="ms-2">
            Diese Seite läuft noch auf einem älteren Stand. Bitte neu laden — ungespeicherte
            Eingaben gehen dabei verloren.
          </span>
        }
        <button
          type="button"
          class="update-banner__btn ms-3"
          [disabled]="laedtNeu()"
          (click)="neuLaden()"
        >
          {{ laedtNeu() ? 'Wird neu geladen…' : 'Jetzt neu laden' }}
        </button>
      </div>
    }
  `,
  styles: [
    `
    .update-banner {
      position: sticky; top: 0; z-index: 1100;
      width: 100%; padding: 0.5rem 1rem;
      text-align: center; font-size: 0.95rem;
      background: #BEE3F8; color: #2C5282;
    }
    .update-banner__btn {
      border: 1px solid currentColor; border-radius: 0.25rem;
      background: transparent; color: inherit;
      padding: 0.125rem 0.75rem; font-weight: 600; cursor: pointer;
    }
    .update-banner__btn:hover { background: rgb(255 255 255 / 45%); }

    /* Schmale Viewports (Befund 2026-08-10, am Geraet nachgemessen):
       Inline war der Knopf 137x29 px — 29 px Hoehe liegt deutlich unter den
       empfohlenen 44 px fuer Touch-Ziele, und er klebte am Ende des
       umgebrochenen Textes statt erkennbar fuer sich zu stehen. Mit Maus
       unkritisch, auf dem Telefon ein echtes Treffer-Problem.
       Deshalb hier: eigene Zeile, mittig, ausreichende Hoehe. */
    @media (max-width: 576px) {
      .update-banner__btn {
        display: block;
        margin: 0.5rem auto 0;
        min-height: 44px;
        padding: 0.5rem 1.25rem;
      }
      /* Bootstrap-Utility ms-3 wuerde den Knopf sonst aus der Mitte schieben. */
      .update-banner__btn.ms-3 { margin-left: auto !important; }
    }
  `,
  ],
})
export class UpdateAvailableBannerComponent implements OnInit {
  private readonly svc = inject(DeployVersionService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  readonly updateVerfuegbar = this.svc.updateVerfuegbar;

  /** Laeuft das Aufraeumen vor dem Reload? Sperrt den Knopf und benennt ihn um. */
  readonly laedtNeu = signal(false);

  /**
   * Kennung, die ein vorheriger Reload-Versuch in DIESEM Tab erreichen wollte.
   * Einmalig beim Erzeugen gelesen — der Wert beschreibt das vorige Seitenleben.
   */
  private readonly gemerktesZiel = this.zielLesen();

  /** Siehe {@link reloadWarWirkungslos} — steuert den zweiten Banner-Text. */
  readonly reloadWirkungslos = computed<boolean>(() =>
    reloadWarWirkungslos(this.gemerktesZiel, this.svc.deployed(), this.svc.updateVerfuegbar()),
  );

  /**
   * Beide Kennungen als Tooltip am Streifen. Kostet nichts und macht eine
   * Nutzermeldung sofort auswertbar, statt sie zur Rueckfrage zu machen — genau
   * daran hing die Diagnose am 2026-08-10. Vollstaendig, nicht gekuerzt: Wer das
   * vorliest oder kopiert, braucht den ganzen Wert.
   */
  readonly kennungen = computed<string>(
    () => `Diese Seite: ${this.svc.eigene} · Server: ${this.svc.deployed() ?? 'unbekannt'}`,
  );

  ngOnInit(): void {
    this.pruefen();
    const interval = setInterval(() => this.pruefen(), POLL_INTERVALL_MS);

    // ZUSAETZLICH beim Sichtbarwerden pruefen — nicht nur per Intervall.
    //
    // WARUM (Befund bei der Abnahme am 2026-08-09): Ein reines `setInterval`
    // versagt genau im Szenario, fuer das dieses Banner gebaut ist. Browser
    // drosseln Timer in Hintergrund-Tabs massiv und pausieren sie, wenn der
    // Rechner in Standby geht. Beim ersten Abnahmeversuch war der Tab offen,
    // die Sitzung lebte, die Kennungen unterschieden sich — und der Streifen
    // erschien nicht, weil der Rechner zwischenzeitlich geschlafen hatte. Der
    // Timer hatte einfach nicht gefeuert.
    //
    // Genau dann braucht es die Pruefung aber: Der Nutzer kommt an den Rechner
    // zurueck und arbeitet weiter — auf einem veralteten Stand. `visibilitychange`
    // feuert beim Zurueckkehren zum Tab und nach dem Aufwachen, `focus` deckt
    // Fensterwechsel innerhalb desselben Tabs ab. Beide sind guenstig: Die
    // Pruefung ist ein GET auf einen winzigen Endpoint, und `pruefen()` bricht
    // ohne Session sofort ab.
    const beiSichtbar = (): void => {
      if (document.visibilityState === 'visible') this.pruefen();
    };
    document.addEventListener('visibilitychange', beiSichtbar);
    window.addEventListener('focus', beiSichtbar);

    this.destroyRef.onDestroy(() => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', beiSichtbar);
      window.removeEventListener('focus', beiSichtbar);
    });
  }

  /**
   * Reload, der so viel Cache umgeht, wie einer Seite ohne Nutzergeste moeglich
   * ist.
   *
   * WARUM DIESE FASSUNG (Meldung 2026-08-10): Vorher stand hier nur
   * `location.replace()` mit Cache-Buster. Ein Nutzer klickte mehrfach, der
   * Streifen blieb, erst Strg+Shift+R half. Der Cache-Buster erzwingt naemlich
   * nur ein frisches DOKUMENT — er raeumt nichts weg, was schon anderswo liegt.
   *
   * Reihenfolge, jeder Schritt mit eigenem Zweck:
   *   1. Cache Storage leeren und Service Worker abmelden ({@link
   *      browserCachesLeeren}) — heute ohne Wirkung, weil die App keinen SW
   *      registriert, aber die naechste PWA-Schicht waere sonst die naechste
   *      Quelle alter Chunks.
   *   2. Ziel merken: Welche Kennung wollten wir erreichen? Steht der
   *      Unterschied danach unveraendert an, sagt das Banner das offen statt die
   *      gleiche wirkungslose Aufforderung zu wiederholen.
   *   3. Dokument mit `cache: 'reload'` holen — das ist der Teil, den ein
   *      Query-Parameter NICHT leistet: Der Browser umgeht dabei seinen
   *      HTTP-Cache und ersetzt den Eintrag. Scheitert es, wird trotzdem
   *      navigiert.
   *   4. Navigieren, mit Cache-Buster und unter Beibehaltung der aktuellen
   *      Route — der Nutzer soll nicht auch noch seine Stelle verlieren.
   *
   * `laedtNeu` sperrt waehrenddessen den Knopf: Vorher war Schritt 1 nicht da
   * und der Klick fuehrte sofort zur Navigation; jetzt liegt Arbeit davor, und
   * ein Knopf ohne Rueckmeldung wuerde wieder wie ein kaputter Knopf wirken.
   */
  async neuLaden(): Promise<void> {
    if (this.laedtNeu()) return;
    this.laedtNeu.set(true);

    const stempel = Date.now();
    const ziel = mitCacheBuster(window.location.href, stempel);

    await browserCachesLeeren(
      typeof window.caches !== 'undefined' ? window.caches : undefined,
      typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
    );

    this.zielSchreiben(this.svc.deployed());

    try {
      await fetch(ziel, { cache: 'reload', credentials: 'include' });
    } catch {
      /* bewusst ignoriert — Navigation ist der Zweck, nicht dieser Vorlauf */
    }

    window.location.replace(ziel);
  }

  /**
   * Nur fuer eingeloggte Sitzungen: `/api/v1/version` ist
   * IS_AUTHENTICATED_FULLY-geschuetzt, ein Poll ohne Session erzeugte nur
   * 401er. Fehler werden bewusst verworfen — ein nicht erreichbarer Endpoint
   * darf keinen Hinweis ausloesen (siehe DeployVersionService).
   */
  private pruefen(): void {
    if (!this.auth.isAuthenticated()) return;
    this.svc.refresh().subscribe({
      next: () => {
        // Stimmen die Kennungen wieder, ist der Marker erledigt — sonst wuerde
        // ein spaeterer, echter Hinweis sofort als „wirkungslos" erscheinen.
        if (!this.svc.updateVerfuegbar()) this.zielSchreiben(null);
      },
      error: () => {
        /* bewusst ignoriert */
      },
    });
  }

  /**
   * sessionStorage-Zugriffe gekapselt, weil sie werfen koennen: In Safaris
   * privatem Modus und bei per Richtlinie abgeschaltetem Storage ist schon der
   * Lesezugriff eine Exception. Der Hinweis muss dann trotzdem funktionieren —
   * ohne den Zusatztext, aber ohne Absturz.
   */
  private zielLesen(): string | null {
    try {
      return window.sessionStorage.getItem(RELOAD_ZIEL_KEY);
    } catch {
      return null;
    }
  }

  private zielSchreiben(ziel: string | null): void {
    try {
      if (ziel === null) window.sessionStorage.removeItem(RELOAD_ZIEL_KEY);
      else window.sessionStorage.setItem(RELOAD_ZIEL_KEY, ziel);
    } catch {
      /* siehe zielLesen() */
    }
  }
}
